import { createSelector } from 'reselect'
import type { ExplorerPane } from 'types/explorer'
import type { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import type { GroupContents } from 'types/shareable-channel'
import type { Shortlist } from 'types/shortlist'
import type { Track, TrackId } from 'types/track'
import { TRACK } from '@disco/constants/ItemTypes'
import type {
  TrackStatus,
  WaveformStatus,
} from '@disco/state-management/reducers/media'
import { isPlayableTrack, isAggregatedSearchTrack } from '@disco/utils/tracks'
import { getSearchState, getGroupContent } from './shareable-channels'
import { getPlaylist, getPlaylistWithTracks } from './playlists'
import { getShortlistById } from './shortlists'
import { getTrack, getTracks } from './tracks'

export const SEARCH_PAGE_LOCATION = 'Search page' as const

/**
 * Known playback locations for type-safe routing.
 * These values are used in trackStatus.playbackLocation to determine
 * which next/prev handler to use in MediaActions.
 */
export type PlaybackLocation = typeof SEARCH_PAGE_LOCATION
/**
 * Gets a shortlist filtered for media playback (only playable tracks).
 * @param state - The Redux state
 * @param props - Object containing the shortlist ID
 * @returns Shortlist with only playable tracks, or null if shortlist not found
 */
export const getShortlistForMediaPlayback = createSelector(
  getShortlistById,
  shortlist => {
    if (!shortlist) return null
    return {
      ...shortlist,
      items: shortlist.items.filter(item => isPlayableTrack(item.track)),
    }
  },
)
/**
 * Gets a playlist filtered for media playback (only playable tracks).
 * @param state - The Redux state
 * @param props - Object containing the playlist ID
 * @returns Playlist with only playable tracks, or null if playlist not found
 */
export const getPlaylistForMediaPlayback = createSelector(
  getPlaylistWithTracks,
  playlist => {
    if (!playlist) return null
    return {
      ...playlist,
      tracks: playlist.tracks.filter(track => isPlayableTrack(track)),
    }
  },
)

/**
 * Filters items to only include playable tracks.
 * @param tracks - The tracks object keyed by track ID
 * @param items - Array of items to filter
 * @returns Array of playable track objects
 */
const playbackFilter = (tracks, items) =>
  items
    .filter(item => item.type === TRACK)
    .map(item => tracks[item.id])
    .filter(track => track && isPlayableTrack(track))

/**
 * Gets group contents filtered for playback on channel page.
 * @param state - The Redux state
 * @returns Group contents with only playable tracks, or null if no content
 */
export const getGroupForPlayback = createSelector(
  [getGroupContent, getTracks],
  (content, tracks): GroupContents | null | undefined => {
    if (!content) return null
    // return filtered content for playback
    return { ...content, items: playbackFilter(tracks, content.items) }
  },
)
/**
 * Gets search results for public channels filtered for playback.
 * @param state - The Redux state
 * @returns Search results with only playable tracks, or null if no search results
 */
export const getPublicChannelSearchResults = createSelector(
  [getSearchState, getTracks],
  (
    search,
    tracks,
  ):
    | {
        results: Track[]
        next: string | null | undefined
      }
    | null
    | undefined => {
    if (!search) return null
    // return filtered content for playback
    return {
      ...search,
      results: playbackFilter(tracks, search.results),
      next: search.next,
    }
  },
)

/**
 * Gets the playback pane from the explorer state.
 * @param state - The Redux state
 * @returns The playback pane, or null if no explorer or playback pane
 */
const getPlaybackPane = (state: State): ExplorerPane | null | undefined =>
  state.explorer ? state.explorer.playbackPane : null

/**
 * Gets the currently playing track.
 * @param state - The Redux state
 * @returns The playing track object, or null if no track is playing
 */
export const getTrackPlaying = (state: State): Track | null | undefined => {
  return state.media.trackOriginalId
    ? getTrack(state, {
        id: state.media.trackOriginalId,
      })
    : null
}
/**
 * Gets the playback pane filtered for media playback with aggregated search handling.
 * @param state - The Redux state
 * @returns Playback pane with filtered playable tracks and aggregated search support
 */
export const getPlaybackPaneForMediaPlayback = createSelector(
  [getPlaybackPane, getTracks, getTrackPlaying],
  (pane: ExplorerPane | null | undefined, tracks, trackPlaying) => {
    if (!pane) return null
    const aggregatedSearchResults = pane.aggregatedSearchResults
    const isPlayingAggregatedSearchTrack = trackPlaying
      ? isAggregatedSearchTrack(trackPlaying)
      : false
    let results = pane.results
    let next = pane.next

    if (aggregatedSearchResults && isPlayingAggregatedSearchTrack) {
      results = aggregatedSearchResults.searchResults
      next = aggregatedSearchResults.next
    }

    return {
      ...pane,
      isPlayingAggregatedSearchTrack: isPlayingAggregatedSearchTrack,
      results: (results ?? [])
        .map(r => tracks[r.id])
        .filter(p => p && p.type === TRACK && isPlayableTrack(p)),
      next,
    }
  },
)
/**
 * Gets the track that is currently being preloaded.
 * @param state - The Redux state
 * @returns The preloading track object, or null if no track is preloading
 */
export const getPreloadingTrack = (state: State): Track | null | undefined => {
  return state.media.preloadingTrackId
    ? getTrack(state, {
        id: state.media.preloadingTrackId,
      })
    : null
}
/**
 * Gets the playlist that is currently playing.
 * @param state - The Redux state
 * @returns The playing playlist object, or null if no playlist is playing
 */
export const getPlayingPlaylist = (
  state: State,
): Playlist | null | undefined => {
  return state.media.trackPlaylistId
    ? getPlaylist(state, {
        id: state.media.trackPlaylistId,
      })
    : null
}
/**
 * Gets the next video track in the currently playing playlist.
 * @param state - The Redux state
 * @returns The next video track ID, or null if no next video available
 */
export const getNextVideoInPlaylist = createSelector(
  [getTrackPlaying, getPlayingPlaylist, getTracks],
  (track, playlist, allTracks) => {
    if (track && playlist && playlist.tracks) {
      const currentTrackIndex = playlist.tracks.findIndex(
        // @ts-expect-error todo fix later
        trackId => trackId === track.id,
      )

      if (
        currentTrackIndex > -1 &&
        currentTrackIndex < playlist.tracks.length - 1
      ) {
        const nextVideo = playlist.tracks
          .slice(currentTrackIndex + 1)
          // @ts-expect-error todo fix later
          .find(trackId => allTracks[trackId].video)
        return nextVideo || null
      }
    }

    return null
  },
)
/**
 * Gets the previous video track in the currently playing playlist.
 * @param state - The Redux state
 * @returns The previous video track ID, or null if no previous video available
 */
export const getPreviousVideoInPlaylist = createSelector(
  [getTrackPlaying, getPlayingPlaylist, getTracks],
  (track, playlist, allTracks) => {
    if (track && playlist && playlist.tracks) {
      const currentTrackIndex = playlist.tracks.findIndex(
        // @ts-expect-error todo fix later
        trackId => trackId === track.id,
      )

      if (currentTrackIndex > 0) {
        const prevVideo = playlist.tracks
          .slice(0, currentTrackIndex)
          .reverse()
          // @ts-expect-error todo fix later
          .find(trackId => allTracks[trackId].video)
        return prevVideo || null
      }
    }

    return null
  },
)
/**
 * Gets the shortlist that is currently playing.
 * @param state - The Redux state
 * @returns The playing shortlist object, or null if no shortlist is playing
 */
export const getPlayingShortlist = (
  state: State,
): Shortlist | null | undefined =>
  state.media.trackShortlistId
    ? state.shortlists.byId[state.media.trackShortlistId]
    : null
/**
 * Gets the playback status for a specific track.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.trackId - The track ID to get status for
 * @returns The track status object, or null if track ID is null or status not found
 */
export const getTrackPlaybackStatus = (
  state: State,
  {
    trackId,
  }: {
    trackId: number | null | undefined
  },
): TrackStatus | null | undefined => {
  return trackId ? state.media.trackStatus[trackId] : null
}

/**
 * Selects the playback status for a specific track.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.trackId - The track ID to get playback status for
 * @returns The playback status, or null if not found
 */
export const selectTrackPlaybackStatus = (
  state: State,
  {
    trackId,
  }: {
    trackId: number
  },
): TrackStatus['playbackStatus'] => {
  return state.media.trackStatus[trackId]?.playbackStatus ?? null
}
/**
 * Gets the waveform status for a specific track.
 * @param state - The Redux state
 * @param props - Object containing the track ID
 * @param props.trackId - The track ID to get waveform status for
 * @returns The waveform status object
 */
export const getTrackWaveformStatus = (
  state: State,
  {
    trackId,
  }: {
    trackId: number
  },
): WaveformStatus => {
  return state.media.trackWaveformStatus[trackId]
}
/**
 * Checks if the queue page is currently loading.
 * @param state - The Redux state
 * @returns True if queue page is loading, false otherwise
 */
export const getQueuePageLoading = (state: State): boolean =>
  state.media.trackPlaylistPageLoading
/**
 * Factory function that creates a selector to get track playback status.
 * @returns A selector function for getting track playback status
 */
export const makeGetTrackPlaybackStatus = (): typeof getTrackPlaybackStatus =>
  getTrackPlaybackStatus

/**
 * Gets the current playback location from the playing track's status.
 * Only returns known PlaybackLocation values; unknown strings return null.
 * @param state - The Redux state
 * @returns The playback location, or null if not set or unknown
 */
export const getPlaybackLocation = (state: State): PlaybackLocation | null => {
  const trackId = state.media.trackOriginalId
  if (!trackId) return null
  const location = state.media.trackStatus[trackId]?.playbackLocation
  // Runtime check instead of unsafe cast - returns null for unknown locations
  if (location === SEARCH_PAGE_LOCATION) return location
  return null
}

/**
 * Checks if the current playback is from the search page.
 * Memoized to prevent re-renders on unrelated state changes.
 * @param state - The Redux state
 * @returns True if playing from search page, false otherwise
 */
export const isSearchPagePlayback = createSelector(
  [getPlaybackLocation],
  (playbackLocation): boolean => playbackLocation === SEARCH_PAGE_LOCATION,
)

/**
 * Checks if the search page is currently loading the next page.
 * @param state - The Redux state
 * @returns True if loading next page, false otherwise
 */
export const selectSearchPageIsLoadingNextPage = (state: State): boolean =>
  state.searchPage?.isLoadingNextPage ?? false

/**
 * Gets the search page error message.
 * @param state - The Redux state
 * @returns The error message, or null if no error
 */
export const selectSearchPageError = (state: State): string | null =>
  state.searchPage?.error ?? null

/**
 * Gets tracks for search page result IDs only.
 * Scoped to avoid recomputation on unrelated track updates.
 */
const getSearchPageTracks = createSelector(
  [
    (state: State) => state.searchPage?.results ?? [],
    (state: State) => state.tracks,
  ],
  (resultIds, tracks): Track[] =>
    resultIds.map((id: TrackId) => tracks[id]).filter(Boolean),
)

/**
 * Gets the playable track IDs from search page results.
 * Used to snapshot the tracklist when starting playback from search.
 * @param state - The Redux state
 * @returns Array of playable track IDs
 */
export const selectSearchPagePlayableTrackIds = createSelector(
  [getSearchPageTracks],
  (searchTracks): TrackId[] =>
    searchTracks.filter(isPlayableTrack).map(track => track.id),
)

/**
 * Gets the search page results for playback when playing from search page.
 * Returns filtered playable tracks with pagination info.
 * @param state - The Redux state
 * @returns Search page results with playable tracks and pagination, or null if not playing from search
 */
export const getSearchPageResultsForPlayback = createSelector(
  [
    getSearchPageTracks,
    isSearchPagePlayback,
    (state: State) => state.searchPage?.hasMore ?? false,
  ],
  (
    searchTracks,
    isFromSearchPage,
    hasMore,
  ): { results: Track[]; hasMore: boolean } | null => {
    if (!isFromSearchPage) return null
    return {
      results: searchTracks.filter(
        (track: Track) => track && isPlayableTrack(track),
      ),
      hasMore,
    }
  },
)
